import { DEFAULT_MAPS_SELECTED, DEFAULT_WINGMAN_MAPS_SELECTED } from "../../constants/maps";
import { TIERS, TIER_LIMITS } from "../../constants/membership";

function canTierRefineSelections(tier) {
    const limits = TIER_LIMITS[tier] || TIER_LIMITS[TIERS.FREE];
    return Boolean(limits.canEditMatchmakingSettings);
}

/**
 * Resolve which tier owns the lobby/solo map & region selection.
 * In a party, the host's plan drives effective free-tier "all selected" behavior.
 */
export function resolveSelectionOwnerTier({
    currentLobbyCode,
    currentLobbyData,
    viewerTier,
} = {}) {
    if (!currentLobbyCode || !currentLobbyData) {
        return viewerTier || TIERS.FREE;
    }

    const hostId = currentLobbyData.hostId;
    const hostMember = Array.isArray(currentLobbyData.members)
        ? currentLobbyData.members.find((member) => member?.userId === hostId)
        : null;

    return (
        hostMember?.membershipTier ||
        hostMember?.membership?.tier ||
        viewerTier ||
        TIERS.FREE
    );
}

/**
 * Mirror edge getEffectiveSelectedMaps for UI: free (or locked) tiers always
 * show the full map pool as selected, even when the lobby document stores [].
 */
export function resolveLobbyMapsToDisplay({
    currentLobbyCode,
    personalMaps,
    lobbyMaps,
    selectionOwnerTier,
    queueMode,
} = {}) {
    const allMaps = queueMode === "wingman"
        ? DEFAULT_WINGMAN_MAPS_SELECTED
        : DEFAULT_MAPS_SELECTED;

    if (!canTierRefineSelections(selectionOwnerTier)) {
        return [...allMaps];
    }

    const source = !currentLobbyCode ? personalMaps : lobbyMaps;
    // Empty array is a real selection state (e.g. after a failed deselect race).
    // Only fall back to all maps when the selection is missing entirely.
    if (Array.isArray(source)) {
        return source;
    }
    return [...allMaps];
}

/**
 * Mirror edge getEffectiveSelectedLocations for UI: empty or missing
 * selections fall back to every enabled region the owner can use.
 */
export function resolveLobbyLocationsToDisplay({
    currentLobbyCode,
    personalLocations,
    lobbyLocations,
    enabledLocationIds,
    selectionOwnerTier,
} = {}) {
    const allEnabled = Array.isArray(enabledLocationIds)
        ? enabledLocationIds.filter(Boolean)
        : [];

    if (!canTierRefineSelections(selectionOwnerTier)) {
        return [...allEnabled];
    }

    const source = !currentLobbyCode ? personalLocations : lobbyLocations;
    if (!Array.isArray(source) || source.length === 0) {
        return [...allEnabled];
    }
    return source;
}
